import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR, fmtDate, apiUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
    MagnifyingGlass, FilePdf, Eye, ArrowsClockwise, Sparkle, Brain
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
    facture: "Facture",
    bon_livraison: "Bon de livraison",
    bon_intervention: "Bon d'intervention",
    bon_de_commande: "Bon de commande",
    devis: "Devis",
    devis_accepte: "Devis accepté",
    avoir: "Avoir",
    traite: "Traite",
    autre: "Autre",
    justificatif: "Justificatif",
    missing: "Aucun doc.",
    unknown: "Inconnu",
};

export default function Search() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reindexing, setReindexing] = useState(false);
    const [reclassifyState, setReclassifyState] = useState(null); // {status, total, processed, reclassified, errors_count, …}

    const runSearch = useCallback(async () => {
        const q = query.trim();
        if (q.length < 2) {
            toast.error("Tapez au moins 2 caractères");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
            setResults(data);
        } catch (e) {
            toast.error("Erreur lors de la recherche");
            setResults([]);
        } finally {
            setBusy(false);
        }
    }, [query]);

    const reindex = async () => {
        setReindexing(true);
        try {
            const { data } = await api.post("/search/reindex");
            toast.success(`${data.indexed} factures réindexées (${data.skipped} ignorées)`);
        } catch (e) {
            toast.error("Erreur lors de la réindexation");
        } finally {
            setReindexing(false);
        }
    };

    // ----- LLM re-classification (background job, polled) -----
    const reclassifyRunning = reclassifyState?.status === "running";

    const pollReclassify = useCallback(async () => {
        try {
            const { data } = await api.get("/search/reclassify/state");
            setReclassifyState(data);
            return data?.status;
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        // Pick up an in-progress run on page load
        pollReclassify();
    }, [pollReclassify]);

    useEffect(() => {
        if (!reclassifyRunning) return;
        const id = setInterval(async () => {
            const st = await pollReclassify();
            if (st && st !== "running") clearInterval(id);
        }, 2500);
        return () => clearInterval(id);
    }, [reclassifyRunning, pollReclassify]);

    const startReclassify = async () => {
        if (!window.confirm(
            "Re-passer le LLM (Gemini) sur TOUS vos PDFs ?\n\n" +
            "Cela peut prendre plusieurs minutes selon le nombre de documents. " +
            "Le LLM va re-extraire le type de document, le bloc intervention et les totaux. " +
            "Cette action consomme du crédit Emergent LLM."
        )) return;
        try {
            await api.post("/search/reclassify");
            toast.success("Re-classification LLM démarrée — la progression s'affiche en temps réel.");
            pollReclassify();
        } catch (e) {
            if (e.response?.status === 409) {
                toast.info("Une re-classification est déjà en cours.");
                pollReclassify();
            } else {
                toast.error("Impossible de démarrer la re-classification");
            }
        }
    };

    const filteredResults = useMemo(() => {
        if (!results) return null;
        if (typeFilter === "all") return results;
        return results.filter((r) => (r.document_type || "facture") === typeFilter);
    }, [results, typeFilter]);

    const goToList = (docType) => {
        if (docType === "bon_livraison") navigate("/bons-livraison");
        else if (docType === "bon_intervention") navigate("/bons-intervention");
        else if (docType === "devis_accepte") navigate("/devis-acceptes");
        else navigate("/factures");
    };

    const highlight = (text, q) => {
        if (!text || !q) return text;
        const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
        return parts.map((p, i) =>
            p.toLowerCase() === q.toLowerCase()
                ? <mark key={i} className="bg-amber-200 text-foreground px-0.5 rounded">{p}</mark>
                : <span key={i}>{p}</span>
        );
    };

    return (
        <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <p className="overline">Recherche</p>
                    <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
                        Moteur de recherche
                    </h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
                        Recherche full-text dans le contenu de vos factures PDF : n° de commande, articles, vendeur,
                        montants — partout dans le contenu extrait des PDFs.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={reindex}
                        disabled={reindexing}
                        data-testid="reindex-btn"
                    >
                        <ArrowsClockwise size={16} className={`mr-2 ${reindexing ? "animate-spin" : ""}`} />
                        {reindexing ? "Réindexation…" : "Réindexer les PDFs"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={startReclassify}
                        disabled={reclassifyRunning}
                        className="border-violet-300 text-violet-700 hover:bg-violet-50"
                        data-testid="llm-reclassify-btn"
                        title="Re-passer Gemini sur tous vos PDFs (extrait le bloc intervention, requalifie BL/BI/Devis acceptés)"
                    >
                        <Brain size={16} className={`mr-2 ${reclassifyRunning ? "animate-pulse" : ""}`} />
                        {reclassifyRunning ? "Re-classification LLM…" : "Re-classifier au LLM"}
                    </Button>
                </div>
            </div>

            {reclassifyState && reclassifyState.status !== "idle" && (
                <div
                    className={
                        "mb-6 rounded-md border p-3 text-sm " +
                        (reclassifyState.status === "running"
                            ? "bg-violet-50 border-violet-200 text-violet-900"
                            : reclassifyState.status === "error"
                                ? "bg-red-50 border-red-200 text-red-900"
                                : "bg-emerald-50 border-emerald-200 text-emerald-900")
                    }
                    data-testid="llm-reclassify-state"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Brain size={18} className={reclassifyState.status === "running" ? "animate-pulse" : ""} />
                            <strong>
                                {reclassifyState.status === "running" && "Re-classification LLM en cours…"}
                                {reclassifyState.status === "done" && "Re-classification LLM terminée"}
                                {reclassifyState.status === "error" && "Re-classification LLM échouée"}
                            </strong>
                        </div>
                        <span className="font-mono text-xs">
                            {reclassifyState.processed || 0} / {reclassifyState.total || 0}
                            {" · "}{reclassifyState.reclassified || 0} requalifiés
                            {reclassifyState.errors_count > 0 && ` · ${reclassifyState.errors_count} erreurs`}
                        </span>
                    </div>
                    {reclassifyState.status === "running" && reclassifyState.total > 0 && (
                        <div className="mt-2 h-1.5 w-full rounded-full bg-violet-200 overflow-hidden">
                            <div
                                className="h-full bg-violet-600 transition-all"
                                style={{ width: `${Math.round((reclassifyState.processed / reclassifyState.total) * 100)}%` }}
                            />
                        </div>
                    )}
                    {reclassifyState.last_error && (
                        <div className="mt-2 text-xs text-red-700/80 truncate">
                            Dernière erreur : {reclassifyState.last_error}
                        </div>
                    )}
                </div>
            )}

            <Card className="ec-card mb-6">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <MagnifyingGlass size={18} className="absolute left-3 top-3 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                                placeholder="Ex : « casque », « anker », un n° de commande, un montant, une référence…"
                                className="pl-10 h-12 text-base"
                                data-testid="search-input"
                                autoFocus
                            />
                        </div>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-52 h-12" data-testid="search-type-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                <SelectItem value="facture">Factures</SelectItem>
                                <SelectItem value="bon_livraison">Bons de livraison</SelectItem>
                                <SelectItem value="bon_intervention">Bons d&apos;intervention</SelectItem>
                                <SelectItem value="avoir">Avoirs</SelectItem>
                                <SelectItem value="devis">Devis</SelectItem>
                                <SelectItem value="devis_accepte">Devis acceptés</SelectItem>
                                <SelectItem value="bon_de_commande">Bons de commande</SelectItem>
                                <SelectItem value="traite">Traites</SelectItem>
                                <SelectItem value="autre">Autres</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={runSearch}
                            disabled={busy}
                            className="bg-primary hover:bg-primary/90 h-12 px-6"
                            data-testid="search-submit"
                        >
                            {busy ? "Recherche…" : "Rechercher"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {results === null && (
                <div className="text-center py-16 px-6 border border-dashed border-border rounded-md">
                    <Sparkle size={32} weight="duotone" className="text-primary mx-auto mb-3" />
                    <h3 className="font-display text-lg font-bold mb-2">Cherchez n’importe quoi dans vos factures</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Tapez une référence, un montant, le nom d’un article, un vendeur — la recherche
                        couvre toutes les données extraites des PDFs.
                    </p>
                </div>
            )}

            {results !== null && filteredResults.length === 0 && (
                <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-md">
                    Aucun document ne contient « {query} »{typeFilter !== "all" ? ` parmi les ${DOC_TYPE_LABELS[typeFilter]?.toLowerCase()}s` : ""}.
                </div>
            )}

            {results !== null && filteredResults.length > 0 && (
                <div className="space-y-3">
                    <div className="text-sm text-muted-foreground mb-1" data-testid="search-result-count">
                        {filteredResults.length} résultat{filteredResults.length > 1 ? "s" : ""}
                        {typeFilter !== "all" && ` · ${DOC_TYPE_LABELS[typeFilter] || typeFilter}`}
                    </div>
                    {filteredResults.map((r) => (
                        <Card key={r.id} className="ec-card ec-card-hover" data-testid={`result-${r.id}`}>
                            <CardContent className="p-4 flex items-start gap-4">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        {r.document_type && (
                                            <Badge variant="outline" className="text-xs bg-accent/40 font-semibold" data-testid={`result-type-${r.id}`}>
                                                {DOC_TYPE_LABELS[r.document_type] || r.document_type}
                                            </Badge>
                                        )}
                                        <span className="font-mono text-xs text-muted-foreground">{r.order_id}</span>
                                        {r.invoice_number && (
                                            <Badge variant="outline" className="text-xs">{r.invoice_number}</Badge>
                                        )}
                                        <Badge variant="outline" className="text-xs">{fmtDate(r.order_date)}</Badge>
                                        {r.seller && <span className="text-xs text-muted-foreground">· {r.seller}</span>}
                                    </div>
                                    {r.snippet && (
                                        <p className="text-sm text-foreground leading-relaxed line-clamp-3">
                                            {highlight(r.snippet, query)}
                                        </p>
                                    )}
                                    {r.matched_in?.length > 0 && (
                                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                                            <span className="overline text-[10px]">Trouvé dans :</span>
                                            {r.matched_in.map((m) => (
                                                <Badge key={m} variant="outline" className="text-[10px] font-normal">{m}</Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <div className="text-right">
                                        <div className="font-display text-lg font-bold tabular">{fmtEUR(r.total_ttc, r.currency)}</div>
                                    </div>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => goToList(r.document_type)}
                                        title="Voir dans la liste"
                                        data-testid={`view-${r.id}`}
                                    >
                                        <Eye size={16} />
                                    </Button>
                                    {r.has_pdf && (
                                        <a
                                            href={apiUrl(`/invoices/${r.id}/pdf`)}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`pdf-${r.id}`}
                                        >
                                            <Button size="icon" variant="ghost">
                                                <FilePdf size={16} />
                                            </Button>
                                        </a>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
